import type { PaginationState, SortingState } from '@tanstack/react-table';
import { isEqual } from 'lodash';
import { useMemo } from 'react';
import type { OutpatientClinicTaskListFilter } from '../types/outpatientClinicTaskListFilter';
import { emptyOutpatientClinicTaskListFilter } from '../constants/emptyOutpatientClinicTaskListFilter';
import { useFilteredOutpatientClinicTasks } from './useFilteredOutpatientClinicTasks';
import { useFormattedOutpatientClinicTasks } from './useFormattedOutpatientClinicTasks';
import { useOutpatientClinicTasksMetadata } from './useOutpatientClinicTasksMetadata';

export type UseOutpatientClinicTasksProps = {
    filter: OutpatientClinicTaskListFilter;
    pagination: PaginationState;
    sorting: SortingState;
};

export function useOutpatientClinicTasks({ filter, pagination, sorting }: UseOutpatientClinicTasksProps) {
    const isEmptyFilter = useMemo(() => {
        return isEqual(filter, emptyOutpatientClinicTaskListFilter);
    }, [filter]);

    const { data, isLoading: isLoadingInitial, error: initialError } = useOutpatientClinicTasksMetadata();

    const {
        filteredTasks,
        isLoading: isLoadingFiltered,
        error: filteredError,
        total: filteredTotal,
    } = useFilteredOutpatientClinicTasks({ filter, pagination, sorting });

    const isLoading = isLoadingFiltered || isLoadingInitial;
    const error = isEmptyFilter ? initialError : filteredError;

    const formattedTasks = useFormattedOutpatientClinicTasks(filteredTasks);

    return {
        episodeCategories: data?.episodeCategories ?? [],
        error,
        isLoading,
        practitioners: data?.practitioners ?? [],
        professions: data?.professions ?? [],
        tasks: formattedTasks,
        total: filteredTotal,
        types: data?.types ?? [],
    };
}
